import React, { useEffect, useMemo, useState } from 'react';
import FormError from '../feedback/FormError';
import { toast } from 'react-hot-toast';
import utils from '../../utils';
import adminServices from '../../services/AdminServices';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import LocationForm from '../dashboard/LocationForm';
import { useLoadScript } from '@react-google-maps/api';
import moment from 'moment';
import LocationModal from '../dashboard/LocationModal';
import { FaMapMarkedAlt } from 'react-icons/fa';
import { useAdminData } from '../../context/SessionContext';
import { MdPersonSearch } from "react-icons/md";
import Loading from '../feedback/Loading';
import FetchError from '../feedback/FetchError';
import Empty from '../feedback/Empty';
import NurseRequestTimeline from './Timeline';

const libraries = ['places'];

const formatUserOption = (p) => ({
    value: p.id,
    label: `${p?.user?.full_name} (${p?.user?.email})`,
    data: p,
});

function RequestModal({ action, activeRequest, onReload, setActiveRequest }) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState(0);
    const [location, setLocation] = useState(null);

    const [selectedPatient, setSelectedPatient] = useState(null);
    const [assignedNurse, setAssignedNurse] = useState(null);

    const [urgencyLevel, setUrgencyLevel] = useState('Medium');
    const [status, setStatus] = useState('Pending');
    const [maxDistance, setMaxDistance] = useState(20);
    const [notes, setNotes] = useState('');
    const [selectedSkills, setSelectedSkills] = useState([]);

    const [matchingNurses, setMatchingNurses] = useState([])
    const [matchLoading, setMatchLoading] = useState(false)
    const [matchError, setMatchError] = useState()
    const [hasSearched, setHasSearched] = useState(false)

    const { services } = useAdminData();

    const { isLoaded } = useLoadScript({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
        libraries,
        id: 'google-maps-script',
        async: true,
        defer: true,
    });

    const skillOptions = useMemo(() => {
        return (services || []).map((skill) => ({
            value: skill.id,
            label: skill.name,
        }));
    }, [services]);

    const requestedSkillIds = useMemo(() => {
        if (activeRequest?.requested_skills) {
            return new Set(activeRequest.requested_skills.map(s => s.id));
        }
        return new Set();
    }, [activeRequest?.requested_skills]);

    useEffect(() => {
        if (action === 0 && selectedPatient) {
            if (selectedPatient.location && !location) {
                setLocation(selectedPatient.location);
            }
            if (selectedPatient.conditions && selectedSkills.length === 0) {
                const preselected = selectedPatient.conditions.map(cond => ({
                    value: cond.id,
                    label: cond.name,
                }));
                setSelectedSkills(preselected);
            }
        }
    }, [selectedPatient, action, location, selectedSkills.length]);

    const urgencyOptions = ['Low', 'Medium', 'Urgent'];
    const statusOptions = ['Pending', 'Matched', 'Accepted', 'Cancelled', 'Completed'];

    const tabs = useMemo(() => {
        if (action === 0) {
            return ['Patient', 'Details', 'Location', 'Finish'];
        } else {
            return ['Overview', 'Edit', 'Assign', 'Timeline'];
        }
    }, [action]);

    const clearData = () => {
        setActiveTab(0);
        setSelectedPatient(null);
        setAssignedNurse(null);
        setUrgencyLevel('Medium');
        setStatus('Pending');
        setMaxDistance(20);
        setNotes('');
        setSelectedSkills([]);
        setLocation(null);
        setError(null);
        setActiveRequest?.(null);
        setMatchingNurses([])
        setHasSearched(false)
    };

    useEffect(() => {

        if (action === 1 && activeRequest) {
            setUrgencyLevel(activeRequest.urgency_level || 'Medium');
            setStatus(activeRequest.status || 'Pending');
            setMaxDistance(activeRequest.max_distance_km || 20);
            setNotes(activeRequest.notes || '');
            setLocation(activeRequest.location_preference || null);

            if (activeRequest.requested_skills) {
                setSelectedSkills(
                    activeRequest.requested_skills.map(s => ({ value: s.id, label: s.name }))
                );
            }

            

            const assigned = activeRequest.get_assigned_nurse;
            if (assigned?.nurse) {
                setAssignedNurse(assigned.nurse);
            }
        }
    }, [action, activeRequest]);

    const loadPatients = async (inputValue) => {
        if (!inputValue || inputValue.length < 2) return Promise.resolve([]);
        try {
            const res = await adminServices.getPatients(`?search=${inputValue}`);
            const results = Array.isArray(res.data) ? res.data : (res.data?.results || []);
            return results.map(p => formatUserOption(p));
        } catch {
            return [];
        }
    };

    const handleLocationSuccess = (loc) => setLocation(loc);

    const handleSubmit = (e) => {
        e.preventDefault();
        const closeBtn = document.getElementById('request-create-modal-close-btn');
        setError(null);
        setLoading(true);

        const formData = new FormData();
        formData.append('patient', selectedPatient?.id || activeRequest?.patient?.id);
        formData.append('urgency_level', urgencyLevel);
        formData.append('status', status);
        formData.append('max_distance_km', maxDistance);
        if (notes) formData.append('notes', notes);

        selectedSkills.forEach(opt => formData.append('requested_skills', opt.value));

        let requestPromise;

        if (action === 0) {
            // CREATE MODE
            const patientLocation = selectedPatient?.location;
            const hasUserModifiedLocation =
                location && patientLocation &&
                (location.address !== patientLocation.address ||
                    location.city !== patientLocation.city ||
                    location.county !== patientLocation.county);

            if (!location?.address) {
                toast.error('Please set a valid location');
                setLoading(false);
                return;
            }

            const handleRequestCreation = (locId) => {
                if (locId) formData.append('location_preference', locId);
                if (assignedNurse?.id) formData.append('assigned_nurse', assignedNurse.id);
                return adminServices.addRequest(formData);
            };

            if (patientLocation && !hasUserModifiedLocation) {
                // Use patient's existing location
                requestPromise = handleRequestCreation(patientLocation.id);
            } else {
                // Create or update location
                const locToast = toast.loading('Saving location...');
                let locationPromise;

                if (patientLocation && hasUserModifiedLocation) {
                    // Update patient's location
                    locationPromise = adminServices.updateLocation(patientLocation.id, {
                        address: location.address,
                        city: location.city,
                        county: location.county,
                        lat: location.lat,
                        lng: location.lng,
                    });
                } else {
                    // Create new location
                    locationPromise = adminServices.addLocation(location);
                }

                requestPromise = locationPromise
                    .then(res => {
                        toast.success('Location saved', { id: locToast });
                        return handleRequestCreation(res.data.id);
                    })
                    .catch(err => {
                        toast.error(utils.toastError, { id: locToast });
                        throw err;
                    });
            }
        } else {
            // EDIT MODE 
            const origLoc = activeRequest?.location_preference;
            const hasLocationChanged =
                !origLoc ||
                location?.address !== origLoc.address ||
                location?.city !== origLoc.city ||
                location?.county !== origLoc.county;

            const updateRequest = () => adminServices.updateRequest(activeRequest.id, formData);

            if (!hasLocationChanged) {
                requestPromise = updateRequest();
            } else {
                if (origLoc?.id) {
                    requestPromise = adminServices
                        .updateLocation(origLoc.id, {
                            address: location.address,
                            city: location.city,
                            county: location.county,
                            lat: location.lat,
                            lng: location.lng,
                        })
                        .then(updateRequest);
                } else {
                    requestPromise = adminServices
                        .addLocation(location)
                        .then(res => {
                            formData.append('location_preference', res.data.id);
                            return updateRequest();
                        });
                }
            }
        }

        requestPromise
            .then(() => {
                toast.success(
                    action === 0 ? 'Request created successfully' : 'Request updated successfully'
                );
                setLoading(false);
                onReload?.();
                closeBtn?.click();
            })
            .catch(err => {
                setLoading(false);
                setError(err.response?.data || utils.toastError);
                toast.error(utils.toastError);
            });
    };

    const changeTab = (idx) => {
        if (action === 0 || (action === 1 && idx < 4)) {
            setActiveTab(idx);
        }
    };

    function getMatchingPatients(id) {
        setHasSearched(true)
        const nurseLoading = toast.loading("Fetching matching Nurses...")
        setMatchingNurses([])
        setMatchLoading(false)
        setMatchError()
        adminServices.getRequestNurses(id).then(
            res => {
                console.log(res.data);
                toast.success("Fetched relevant Nurses.", { id: nurseLoading })
                setMatchingNurses(res.data)
                setMatchLoading(false)
            },
            err => {
                console.log(err);
                toast.error(utils.errorMsg, { id: nurseLoading })
                setMatchError(err)
                setMatchLoading(false)
            }
        )
    }

    function checkIsAssignedNurse(id) {
        if (activeRequest.get_assigned_nurse) {
            if (activeRequest.get_assigned_nurse?.nurse?.id === id) {
                return true
            }
        }
        return false
    }
    function assignNurse(id) {
        const closeBtn = document.getElementById('request-create-modal-close-btn');
        if (checkIsAssignedNurse(id)) {
            toast.error("This Nurse is already set")
            return
        }
        console.log("assigning...");
        const data = {
            "request_id": activeRequest?.id,
            "nurse_id": id,
        }
        const loadingToast = toast.loading("Assigning Nurse")
        adminServices.assignNurseToRequest(data).then(
            res => {
                toast.success("Nurse Assigned Successfully", {
                    id: loadingToast
                })
                onReload?.();
                closeBtn?.click();
            },
            err => {
                console.log(err);
                toast.error(utils.toastError, {
                    id: loadingToast
                })
            }
        )

    }

    return (
        <div>
            <label htmlFor="request-create-modal" id="request-create-open-btn" className="hidden">
                Manage Request
            </label>
            <input type="checkbox" id="request-create-modal" className="modal-toggle" />

            <div className="modal">
                <div className="modal-box w-11/12 max-w-5xl">
                    <label
                        onClick={clearData}
                        htmlFor="request-create-modal"
                        id="request-create-modal-close-btn"
                        className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2"
                    >
                        ✕
                    </label>

                    {(action === 1) && (activeRequest?.location_preference?.lat && activeRequest?.location_preference?.lng) ? (
                        <LocationModal
                            activeUser={activeRequest?.patient?.user}
                            lat={activeRequest?.location_preference?.lat}
                            lng={activeRequest?.location_preference?.lng} />
                    ) : null}

                    <h2 className="text-lg font-bold mb-2">
                        {action === 0 ? 'Create New Request' : 'Edit Request'}
                        {action === 1 && activeRequest && (
                            <span className="text-gray-500 ml-2">
                                {activeRequest.patient?.user?.full_name}
                            </span>
                        )}
                    </h2>

                    {/* Tabs */}
                    <div className="flex justify-center mb-6">
                        <div className="border-gray-200 dark:border-border-dark">
                            <div className="tabs tabs-boxed justify-center">
                                {tabs.map((tab, idx) => (
                                    // eslint-disable-next-line jsx-a11y/anchor-is-valid
                                    <a
                                        key={idx}
                                        className={`tab ${activeTab === idx ? 'tab-active bg-primary text-white rounded-lg' : ''}`}
                                        onClick={() => changeTab(idx)}
                                        role="tab"
                                        aria-selected={activeTab === idx}
                                    >
                                        {tab}
                                    </a>
                                ))}
                            </div>
                        </div>
                    </div>

                    {action === 0 && (
                        <>
                            {activeTab === 0 && (
                                <div className="space-y-4 min-h-36">
                                    <h3 className="font-bold text-lg">Select Patient</h3>
                                    <AsyncSelect
                                        loadOptions={loadPatients}
                                        placeholder="Type patient name or email (min. 2 chars)..."
                                        onChange={(opt) => setSelectedPatient(opt ? opt.data : null)}
                                        value={selectedPatient ? formatUserOption(selectedPatient) : null}
                                        noOptionsMessage={() => "Type to search patients"}
                                        isClearable
                                        cacheOptions
                                        defaultOptions
                                        styles={{
                                            control: (base) => ({
                                                ...base,
                                                borderColor: '#d1d5db',
                                            }),
                                        }}
                                    />

                                    {selectedPatient && (
                                        <div className="mt-4 p-3 bg-surface-light rounded-lg border">
                                            <div className="flex gap-3 items-center">
                                                <div className="avatar placeholder">
                                                    {selectedPatient?.user?.image ? (
                                                        <div className="w-12 rounded-full">
                                                            <img src={selectedPatient?.user.image} alt={selectedPatient?.user.full_name} />
                                                        </div>
                                                    ) : (
                                                        <div className="bg-neutral text-white rounded-full w-12 flex items-center justify-center">
                                                            <span className="text-sm">
                                                                {utils.makeProfileLetter(selectedPatient?.user)}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                                <div>
                                                    <p className="font-bold">{selectedPatient?.user?.full_name}</p>
                                                    <p className="text-gray-600 text-sm">{selectedPatient?.user?.email}</p>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {selectedPatient && (
                                        <button
                                            type="button"
                                            onClick={() => setActiveTab(1)}
                                            className="mt-4 btn btn-primary"
                                        >
                                            Continue to Details
                                        </button>
                                    )}
                                </div>
                            )}

                            {activeTab === 1 && (
                                <form onSubmit={(e) => e.preventDefault()} className="space-y-4">
                                    <h3 className="font-bold text-lg">Request Details</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="label">
                                                <span className="label-text">Urgency Level *</span>
                                            </label>
                                            <select
                                                value={urgencyLevel}
                                                onChange={(e) => setUrgencyLevel(e.target.value)}
                                                className="select select-bordered w-full"
                                            >
                                                {urgencyOptions.map(level => (
                                                    <option key={level} value={level}>{level}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="label">
                                                <span className="label-text">Max Search Distance (km)</span>
                                            </label>
                                            <input
                                                type="number"
                                                min="1"
                                                max="100"
                                                value={maxDistance}
                                                onChange={(e) => setMaxDistance(Number(e.target.value))}
                                                className="input input-bordered w-full"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="label">
                                            <span className="label-text">Required Skills</span>
                                        </label>
                                        <Select
                                            isMulti
                                            value={selectedSkills}
                                            onChange={setSelectedSkills}
                                            options={skillOptions}
                                            placeholder="Select one or more skills..."
                                            noOptionsMessage={() => "No skills available. Add categories first."}
                                            styles={{
                                                control: (base) => ({
                                                    ...base,
                                                    borderColor: '#d1d5db',
                                                    '&:hover': { borderColor: '#9ca3af' },
                                                }),
                                            }}
                                        />
                                    </div>

                                    <div>
                                        <label className="label">
                                            <span className="label-text">Notes (Optional)</span>
                                        </label>
                                        <textarea
                                            value={notes}
                                            onChange={(e) => setNotes(e.target.value)}
                                            className="textarea textarea-bordered w-full"
                                            rows="3"
                                        />
                                    </div>

                                    <div className="flex justify-between pt-4">
                                        <button type="button" onClick={() => setActiveTab(0)} className="btn btn-outline">
                                            Back
                                        </button>
                                        <button type="button" onClick={() => setActiveTab(2)} className="btn btn-primary">
                                            Continue to Location
                                        </button>
                                    </div>
                                </form>
                            )}

                            {activeTab === 2 && isLoaded && (
                                <div className="space-y-4">
                                    <h3 className="font-bold text-lg">Service Location</h3>
                                    <LocationForm location={location} action={0} onSuccess={handleLocationSuccess} isRequest={true} />
                                    <div className="flex justify-between pt-4">
                                        <button type="button" onClick={() => setActiveTab(1)} className="btn btn-outline">
                                            Back
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setActiveTab(3)}
                                            disabled={!location?.address}
                                            className="btn btn-primary"
                                        >
                                            Continue to Assignment
                                        </button>
                                    </div>
                                </div>
                            )}

                            {activeTab === 3 && (
                                <div className="space-y-4">
                                    <h3 className="font-bold text-lg">Finish Creating Nurse Request</h3>
                                    <p className="text-gray-500">Make sure all entries are correct and confirmed. *</p>

                                    <FormError error={error} />

                                    <div className="flex justify-between pt-4">
                                        <button type="button" onClick={() => setActiveTab(2)} className="btn btn-outline">
                                            Back
                                        </button>
                                        <button
                                            onClick={handleSubmit}
                                            disabled={loading || !selectedPatient || !location?.address}
                                            className="btn btn-primary"
                                        >
                                            {loading ? 'Creating...' : 'Create Request'}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    {action === 1 && activeRequest && (
                        <>
                            {activeTab === 0 && (
                                <div className="space-y-4">
                                    <div className="border flex-grow border-border-light dark:border-border-dark bg-surface-light dark:bg-surface-dark p-4 md:p-7 shadow-sm transition-all rounded-xl">
                                        <h4 className="font-bold mb-2">Request Overview</h4>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                                            <div className=''>
                                                <p className="font-bold mb-1">Patient Details:</p>
                                                <div>
                                                    <div className="flex gap-3 items-center">
                                                        <div className="avatar placeholder">
                                                            {activeRequest?.patient?.user?.image ? (
                                                                <div className="w-20 rounded-full">
                                                                    <img src={activeRequest?.patient.user.image} alt={activeRequest?.patient.user.full_name} />
                                                                </div>
                                                            ) : (
                                                                <div className="bg-neutral text-white rounded-full w-20">
                                                                    <span className="text-lg">
                                                                        {utils.makeProfileLetter(activeRequest?.patient.user)}
                                                                    </span>
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div>
                                                            {activeRequest?.patient?.user?.full_name && (
                                                                <p className="font-bold">{activeRequest?.patient.user.full_name}</p>
                                                            )}
                                                            <p className="text-gray-600">{activeRequest?.patient?.user?.email}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="">
                                                <p className="font-bold mb-1">Assigned Nurse:</p>
                                                <div className="">
                                                    {activeRequest.get_assigned_nurse?.nurse?.user ? (
                                                        <div className="flex gap-3 items-center">
                                                            <div className="avatar placeholder">
                                                                {activeRequest.get_assigned_nurse.nurse.user.image ? (
                                                                    <div className="w-20 rounded-full">
                                                                        <img
                                                                            src={activeRequest.get_assigned_nurse.nurse.user.image}
                                                                            alt={activeRequest.get_assigned_nurse.nurse.user.full_name}
                                                                            className="rounded-full"
                                                                        />
                                                                    </div>
                                                                ) : (
                                                                    <div className="bg-neutral text-white rounded-full w-20 flex items-center justify-center">
                                                                        <span className="text-lg">
                                                                            {utils.makeProfileLetter(activeRequest.get_assigned_nurse.nurse.user)}
                                                                        </span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div>
                                                                <p className="font-bold">{activeRequest.get_assigned_nurse.nurse.user.full_name}</p>
                                                                <p className="text-gray-600">{activeRequest.get_assigned_nurse.nurse.user.email}</p>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <p className="text-gray-500 italic">Not assigned yet</p>
                                                    )}
                                                </div>
                                            </div>
                                            <p><span className="font-bold">Status:</span> {activeRequest.status}</p>
                                            <p><span className="font-bold">Urgency:</span> {activeRequest.urgency_level}</p>
                                            <p><span className="font-bold">Distance:</span> {activeRequest.max_distance_km} km</p>
                                            <div className='md:col-span-2 flex flex-col md:flex-row md:justify-between md:items-center'>
                                                <div>
                                                    <span className="font-bold">Location:</span> {activeRequest.location_preference?.address || '—'}
                                                </div>
                                                {(activeRequest?.location_preference?.lat && activeRequest?.location_preference?.lng) ? (
                                                    <div>
                                                        <div
                                                            onClick={() => {
                                                                setActiveTab(1)
                                                                document.getElementById('location-open-btn').click();
                                                            }}
                                                            className='btn btn-accent btn-sm text-white'
                                                        >
                                                            <FaMapMarkedAlt /> Open Map
                                                        </div>
                                                    </div>
                                                ) : null}
                                            </div>
                                            <p className="md:col-span-2">
                                                <span className="font-bold">Notes:</span> {activeRequest.notes || '—'}
                                            </p>
                                            <p className="md:col-span-2">
                                                <span className="font-bold">Skills:</span>{' '}
                                                {activeRequest.requested_skills?.map(s => s.name).join(', ') || '—'}
                                            </p>
                                            <p><span className="font-bold">Total Charge:</span> {utils.toCommas(activeRequest?.total || 0)}</p>
                                            <p>Created: <span className="font-bold">{moment(activeRequest.creation_time).format('MMM Do YYYY')}</span></p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {activeTab === 1 && (
                                <form onSubmit={handleSubmit} className="space-y-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="label">
                                                <span className="label-text">Status</span>
                                            </label>
                                            <select
                                                value={status}
                                                onChange={(e) => setStatus(e.target.value)}
                                                className="select select-bordered w-full"
                                            >
                                                {statusOptions.map(st => (
                                                    <option key={st} value={st}>{st}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="label">
                                                <span className="label-text">Urgency Level</span>
                                            </label>
                                            <select
                                                value={urgencyLevel}
                                                onChange={(e) => setUrgencyLevel(e.target.value)}
                                                className="select select-bordered w-full"
                                            >
                                                {urgencyOptions.map(level => (
                                                    <option key={level} value={level}>{level}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="label">
                                            <span className="label-text">Required Skills</span>
                                        </label>
                                        <Select
                                            isMulti
                                            value={selectedSkills}
                                            onChange={setSelectedSkills}
                                            options={skillOptions}
                                        />
                                    </div>

                                    <div>
                                        <label className="label">
                                            <span className="label-text">Notes</span>
                                        </label>
                                        <textarea
                                            value={notes}
                                            onChange={(e) => setNotes(e.target.value)}
                                            className="textarea textarea-bordered w-full"
                                            rows="3"
                                        />
                                    </div>

                                    {isLoaded && (
                                        <LocationForm
                                            location={location}
                                            action={1}
                                            onSuccess={handleLocationSuccess}
                                        />
                                    )}

                                    <FormError error={error} />

                                    <div className="flex justify-between pt-4">
                                        <button type="button" onClick={() => setActiveTab(0)} className="btn btn-outline">
                                            Cancel
                                        </button>
                                        <button type="submit" disabled={loading} className="btn btn-primary">
                                            {loading ? 'Saving...' : 'Update Request'}
                                        </button>
                                    </div>
                                </form>
                            )}

                            {activeTab === 2 && (
                                <div className="space-y-4">
                                    <h3 className="font-bold text-lg">Assigned Nurse</h3>
                                    <div className='text-sm'>
                                        {activeRequest.get_assigned_nurse ? (
                                            <div className='bg-surface-light dark:bg-surface-dark rounded-xl border border-border-light dark:border-border-dark p-3 w-full'>
                                                <div className="flex flex-col md:flex-row gap-3 md:items-center">
                                                    <div className="">
                                                        <div className="flex gap-3 items-center ">
                                                            <div className="avatar placeholder">
                                                                {activeRequest.get_assigned_nurse.nurse.user.image ? (
                                                                    <div className="w-24 rounded-full">
                                                                        <img
                                                                            src={activeRequest.get_assigned_nurse.nurse.user.image}
                                                                            alt={activeRequest.get_assigned_nurse.nurse.user.full_name}
                                                                            className="rounded-full"
                                                                        />
                                                                    </div>
                                                                ) : (
                                                                    <div className="bg-neutral text-white rounded-full w-24 flex items-center justify-center">
                                                                        <span className="text-lg">
                                                                            {utils.makeProfileLetter(activeRequest.get_assigned_nurse.nurse.user)}
                                                                        </span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div>
                                                                <p className="font-bold">{activeRequest.get_assigned_nurse.nurse.user.full_name}</p>
                                                                <p className="text-gray-600">{activeRequest.get_assigned_nurse.nurse.user.email}</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="w-full">
                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                            <p><span className="font-bold">License Number:</span> {activeRequest.get_assigned_nurse?.nurse?.license_number}</p>
                                                            <p><span className="font-bold">Years of Experience:</span> {activeRequest.get_assigned_nurse?.nurse?.years_of_experience}</p>
                                                            <p className='md:col-span-2'>
                                                                <span className="font-bold">Address:</span> {
                                                                    activeRequest.get_assigned_nurse?.nurse?.location?.address || '—'
                                                                }
                                                            </p>
                                                            <p className='md:col-span-2'>
                                                                <span className="font-bold">Skills:</span> {
                                                                    (() => {
                                                                        const conds = activeRequest.get_assigned_nurse?.nurse?.skills || [];
                                                                        if (conds.length === 0) return '—';
                                                                        const displayed = conds.map(c => c.name);
                                                                        return displayed.join(', ');
                                                                    })()
                                                                }
                                                            </p>
                                                            <p><span className="font-bold">Request Count:</span> {activeRequest.get_assigned_nurse?.nurse?.request_count}</p>
                                                        </div>
                                                    </div>
                                                </div>

                                            </div>
                                        ) : (
                                            <div>
                                                <p className="text-gray-500">Not assigned yet</p>
                                            </div>
                                        )}

                                    </div>

                                    <p className='font-bold text-lg'>Get Available Nurses:</p>
                                    {activeRequest.status === "Completed" ? (
                                        <div>
                                            <p className="text-gray-500">This Request has been completed</p>
                                        </div>
                                    ) : null}
                                    {activeRequest.status === "Cancelled" ? (
                                        <div>
                                            <p className="text-gray-500">This Request has been canceled!</p>
                                        </div>
                                    ) : null}
                                    {!(activeRequest.status === "Completed") && !(activeRequest.status === "Cancelled") ? (
                                        <>
                                            <p className='text-gray-500 text-sm'>Nurses must match selection criteria on Conditions and Location. *</p>
                                            <div className="flex flex-col md:flex-row gap-3 justify-between md:items-center">
                                                <button
                                                    disabled={matchLoading}
                                                    onClick={() => getMatchingPatients(activeRequest?.id)}
                                                    className='btn btn-wide btn-primary'>
                                                    <MdPersonSearch className='h-5 w-5' /> {matchLoading ? 'Searching for' : 'Search'} Available Nurses
                                                </button>
                                                {(!matchLoading && !matchError) && hasSearched && (
                                                    <div>
                                                        <p className='text-gray-500 text-lg font-bold'>Count: ({matchingNurses?.length})</p>
                                                    </div>
                                                )}
                                            </div>
                                        </>
                                    ) : null}
                                    {matchLoading && (<Loading />)}
                                    {matchError && (<FetchError error={matchError} />)}
                                    {(!matchLoading && !matchError) && hasSearched && (
                                        <div>
                                            {matchingNurses?.length > 0 ? (
                                                <div className='space-y-2 text-sm'>
                                                    {matchingNurses?.map((nurse, idx) => {
                                                        return <div
                                                            className='bg-surface-light dark:bg-surface-dark rounded-xl border border-border-light dark:border-border-dark p-3 flex flex-col md:flex-row gap-3 md:items-center'
                                                            key={idx}
                                                        >
                                                            {/* Left: Avatar + Name */}
                                                            <div className="flex gap-3 items-center md:w-1/3 lg:w-1/4">
                                                                <div className="avatar placeholder">
                                                                    {nurse?.image ? (
                                                                        <div className="w-16 rounded-full">
                                                                            <img
                                                                                src={utils.baseUrl + nurse.image}
                                                                                alt={nurse?.full_name}
                                                                                className="rounded-full"
                                                                            />
                                                                        </div>
                                                                    ) : (
                                                                        <div className="bg-neutral text-white rounded-full w-20 flex items-center justify-center">
                                                                            <span className="text-lg">
                                                                                {utils.makeProfileLetter(nurse)}
                                                                            </span>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                <div className="">
                                                                    <p className='font-bold'>
                                                                        {nurse?.full_name || '--'}
                                                                    </p>
                                                                    <p className='text-gray-600'>
                                                                        {nurse?.email || '--'}
                                                                    </p>
                                                                </div>
                                                            </div>

                                                            <div className="flex-1">
                                                                <p>
                                                                    <span className="font-bold">Skills:</span>{' '}
                                                                    {nurse?.skills?.length ? (
                                                                        nurse.skills.map((skill, i) => (
                                                                            <span
                                                                                key={skill.id}
                                                                                className={
                                                                                    requestedSkillIds.has(skill.id)
                                                                                        ? 'text-green-600 font-medium'
                                                                                        : 'text-gray-700'
                                                                                }
                                                                            >
                                                                                {skill.name}
                                                                                {i < nurse.skills.length - 1 ? ', ' : ''}
                                                                            </span>
                                                                        ))
                                                                    ) : (
                                                                        '—'
                                                                    )}
                                                                </p>
                                                                <p>
                                                                    <span className="font-bold">Distance Away:</span> {(nurse?.distance || 0).toFixed(2)} Km.
                                                                </p>
                                                            </div>

                                                            <div className="ml-auto">
                                                                <button
                                                                    onClick={() => assignNurse(nurse?.id)}
                                                                    className={checkIsAssignedNurse(nurse?.id)
                                                                        ? 'btn btn-info btn-sm'
                                                                        : 'btn btn-secondary btn-sm'
                                                                    }
                                                                >
                                                                    {checkIsAssignedNurse(nurse?.id) ? 'Assigned' : 'Select Nurse'}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    })}
                                                </div>
                                            ) : (
                                                <div>
                                                    <Empty objectName={"Matching Nurses for Request"} />
                                                </div>
                                            )}
                                        </div>
                                    )}


                                </div>
                            )}

                            {activeTab === 3 && (
                                <NurseRequestTimeline
                                    activeRequest={activeRequest}
                                />
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export default RequestModal;